/**
 * DeltaIndicator - Visualizes the spread between moneyline-implied
 * probability and spread-implied probability for a game.
 *
 * A large delta suggests the two markets disagree - potential edge or risk.
 * This is a heuristic visualization, not a trading signal.
 */

import { cn } from '@/lib/utils';
import type { ConsolidatedGameBooks } from '@/lib/nbaConsolidated/types';
import type { SpreadGameData } from '@/lib/sportsStream/types';

interface DeltaIndicatorProps {
  moneyline: ConsolidatedGameBooks | null;
  spread: SpreadGameData | null;
  className?: string;
}

interface DeltaResult {
  /** Moneyline implied probability (0-100) for the favorite */
  mlProb: number;
  /** Spread implied probability (0-100) for the favorite covering */
  spreadProb: number;
  /** Absolute delta between the two (percentage points) */
  delta: number;
  /** Which team is the favorite */
  favoriteCode: string;
  /** "edge" | "marginal" | "aligned" */
  signal: 'edge' | 'marginal' | 'aligned';
}

function computeDelta(
  moneyline: ConsolidatedGameBooks | null,
  spread: SpreadGameData | null
): DeltaResult | null {
  if (!moneyline || !spread) return null;

  // Get moneyline best YES bids for each team
  const awayYesBid = moneyline.kalshi?.markets.away.yes[0]?.priceCents ?? null;
  const homeYesBid = moneyline.kalshi?.markets.home.yes[0]?.priceCents ?? null;

  if (awayYesBid === null && homeYesBid === null) return null;

  // Determine favorite from moneyline
  const awayProb = awayYesBid ?? (homeYesBid !== null ? 100 - homeYesBid : 50);
  const homeProb = homeYesBid ?? (awayYesBid !== null ? 100 - awayYesBid : 50);

  const isFavoriteAway = awayProb >= homeProb;
  const favoriteCode = isFavoriteAway ? moneyline.awayCode : moneyline.homeCode;
  const favoriteProb = isFavoriteAway ? awayProb : homeProb;

  // Find the closest-to-center spread line for the favorite
  let bestSpreadProb: number | null = null;
  let bestDistance = Infinity;

  for (const [, info] of spread.markets) {
    const teamMatches = info.teamCode.toUpperCase() === favoriteCode.toUpperCase();
    if (!teamMatches) continue;

    const yesBid = info.book.yes[0]?.priceCents ?? null;
    if (yesBid === null) continue;

    // Prefer the spread line closest to 50% (the "main" line)
    const distance = Math.abs(yesBid - 50);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestSpreadProb = yesBid;
    }
  }

  // Also check polymarket main spread
  if (bestSpreadProb === null && spread.polymarketMain) {
    const pm = spread.polymarketMain;
    const favBook =
      (isFavoriteAway && pm.favoriteSide === 'away') ||
      (!isFavoriteAway && pm.favoriteSide === 'home')
        ? pm.markets.favorite
        : pm.markets.underdog;
    bestSpreadProb = favBook.yes[0]?.priceCents ?? null;
  }

  if (bestSpreadProb === null) return null;

  const delta = Math.abs(favoriteProb - bestSpreadProb);

  const signal: DeltaResult['signal'] = delta >= 15 ? 'edge' : delta >= 7 ? 'marginal' : 'aligned';

  return {
    mlProb: favoriteProb,
    spreadProb: bestSpreadProb,
    delta,
    favoriteCode,
    signal,
  };
}

export function DeltaIndicator({ moneyline, spread, className }: DeltaIndicatorProps) {
  const result = computeDelta(moneyline, spread);

  if (!result) {
    return <div className={cn('text-muted-foreground text-xs', className)}>No delta</div>;
  }

  const colorClasses = {
    edge: 'text-positive bg-positive/10 border-positive/30',
    marginal: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
    aligned: 'text-muted-foreground bg-muted/50 border-border',
  };

  const labels = {
    edge: 'EDGE',
    marginal: 'WATCH',
    aligned: 'FLAT',
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span
        className={cn(
          'rounded border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider',
          colorClasses[result.signal]
        )}
      >
        {labels[result.signal]}
      </span>
      <div className="text-muted-foreground flex items-baseline gap-1 text-xs">
        <span>ML {result.mlProb}%</span>
        <span className="text-border">vs</span>
        <span>Spd {result.spreadProb}%</span>
        <span
          className={cn('font-mono font-semibold', {
            'text-positive': result.signal === 'edge',
            'text-yellow-400': result.signal === 'marginal',
            'text-muted-foreground': result.signal === 'aligned',
          })}
        >
          {result.delta > 0 ? '+' : ''}
          {result.delta.toFixed(0)}pp
        </span>
      </div>
    </div>
  );
}
